import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AnyAction } from "redux-saga";
import { ThunkDispatch } from "redux-thunk";

import { GateProps } from "./GateProps";
import { userMe } from "../actions/authActions";
import {
  fetchUserSignatures,
  fetchFeatureFlags,
  setAvifSupport,
} from "../actions/sysActions";
import { useHasLoaded } from "../hooks/requestStatusHooks";
import { AuthState, selectIsAccessTokenExpired } from "../reducers/authReducer";
import { isAvifSupported } from "../utils/browserUtils";

// AuthUserGate should only be rendered if there is an unexpired Access JWT
// It will render a LoadingEl Component until the following conditions are met:
//   1. User has been fetched via /api/users/me/
//   2. User Signatures have been fetched via /api/users/{id}/signatures/
//   3. Feature Flags have been fetched via /api/feature-flags/
//   4. Macro-Code Helpers have been fetched via /api/macro-codes/helpers/
//   5. Maintenance Messages have been fetched via /api/mmaintenance-message/

const AuthUserGate = ({ children, LoadingEl, ErrorEl }: GateProps) => {
  const isAccessTokenExpired = useSelector(selectIsAccessTokenExpired);
  const accessTokenUserId = useSelector(
    ({ auth }: { auth: AuthState }) => auth.access?.user_id
  );
  const userId = useSelector(({ auth }: { auth: AuthState }) => auth.user?.id);
  const userFirstName = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.first_name
  );
  const userLastName = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.last_name
  );
  const userEmail = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.email
  );

  const loadedUser = useHasLoaded(userMe);
  const loadedUserSignatures = useHasLoaded(fetchUserSignatures, {
    userId: accessTokenUserId,
  });
  const loadedFeatureFlags = useHasLoaded(fetchFeatureFlags);

  if (isAccessTokenExpired) {
    throw new Error(
      "AuthUserGate should not be rendered with an expired Access Token"
    );
  }

  if (!accessTokenUserId || (userId && userId !== accessTokenUserId)) {
    throw new Error("The loaded User does not match the Access Token");
  }

  // Set User on Sentry and Datadog
  useEffect(() => {
    const userArgs = {
      id: `${accessTokenUserId}`,
      name:
        [userFirstName, userLastName].filter((val) => !!val).join(" ") ||
        undefined,
      email: userEmail,
    };

    datadogRum.setUser(userArgs);

    Sentry.configureScope((scope) => scope.setUser(userArgs));
  }, [accessTokenUserId, userFirstName, userLastName, userEmail]);

  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;
  const [hasErrors, setHasErrors] = useState(false);
  const errorsRef = useRef(hasErrors);
  errorsRef.current = hasErrors;

  // Unset User on Sentry and Datadog when unmounting (session has ended)
  useEffect(() => {
    isAvifSupported().then((isSupported) => {
      dispatch(setAvifSupport(isSupported));
    });

    Promise.all([
      dispatch(userMe()),
      dispatch(
        fetchUserSignatures({
          userId: accessTokenUserId,
        })
      ),
      dispatch(fetchFeatureFlags()),
    ]).catch(() => {
      setHasErrors(true);
    });

    return () => {
      datadogRum.removeUser();
      Sentry.configureScope((scope) => scope.setUser(null));
    };
  }, []);

  if (hasErrors)
    return <ErrorEl className="AuthUserGate">Failed to load User Data</ErrorEl>;

  if (!loadedUser || !loadedUserSignatures || !loadedFeatureFlags)
    return <LoadingEl className="AuthUserGate">Loading User Data...</LoadingEl>;

  return <>{children}</>;
};

export default AuthUserGate;
